/**
 * Shared CSV parsing and export utilities used across admin pages.
 */

export const CSV_SCHEMA_TYPE_COLUMN = 'HikeStrongCsvType'
export const CSV_SCHEMA_VERSION_COLUMN = 'HikeStrongCsvVersion'

type CsvSchemaValidationOptions = {
  expectedType: string
  currentVersion: number
  entityName: string
  allowLegacy?: boolean
}

type CsvSchemaValidationResult = {
  warning?: string
}

/** Parse a single CSV line respecting quoted fields. */
export const parseCsvLine = (line: string): string[] => {
  const result: string[] = []
  let current = ''
  let inQuotes = false
  let i = 0

  while (i < line.length) {
    const char = line[i]
    const nextChar = line[i + 1]

    if (char === '"' && !inQuotes) {
      inQuotes = true
    } else if (char === '"' && inQuotes && nextChar === '"') {
      current += '"'
      i += 1
    } else if (char === '"' && inQuotes) {
      inQuotes = false
    } else if (char === ',' && !inQuotes) {
      result.push(current)
      current = ''
    } else {
      current += char
    }

    i += 1
  }

  result.push(current)
  return result.map((value) => value.trim())
}

/** Parse a CSV document, including quoted fields that span multiple lines. */
export const parseCsvText = (text: string): string[][] => {
  const rows: string[][] = []
  let currentRow: string[] = []
  let currentValue = ''
  let inQuotes = false

  for (let i = 0; i < text.length; i += 1) {
    const char = text[i]
    const nextChar = text[i + 1]

    if (char === '"' && inQuotes && nextChar === '"') {
      currentValue += '"'
      i += 1
      continue
    }

    if (char === '"') {
      inQuotes = !inQuotes
      continue
    }

    if (char === ',' && !inQuotes) {
      currentRow.push(currentValue.trim())
      currentValue = ''
      continue
    }

    if ((char === '\n' || char === '\r') && !inQuotes) {
      if (char === '\r' && nextChar === '\n') {
        i += 1
      }

      currentRow.push(currentValue.trim())
      if (currentRow.some((value) => value.length > 0)) {
        rows.push(currentRow)
      }

      currentRow = []
      currentValue = ''
      continue
    }

    currentValue += char
  }

  if (currentValue.length > 0 || currentRow.length > 0) {
    currentRow.push(currentValue.trim())
    if (currentRow.some((value) => value.length > 0)) {
      rows.push(currentRow)
    }
  }

  return rows
}

/** Escape a value for CSV output (wrap in quotes if it contains commas, quotes, or newlines). */
export const escapeCsvValue = (value: string): string => {
  if (/[",\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`
  }
  return value
}

/** Normalize text for fuzzy header matching (lowercase, strip non-alphanumeric). */
export const normalizeText = (value: string): string =>
  value.trim().toLowerCase().replace(/[^a-z0-9]/g, '')

export const withCsvSchemaColumns = <T extends string>(
  columns: readonly T[]
): Array<T | typeof CSV_SCHEMA_TYPE_COLUMN | typeof CSV_SCHEMA_VERSION_COLUMN> => [
  CSV_SCHEMA_TYPE_COLUMN,
  CSV_SCHEMA_VERSION_COLUMN,
  ...columns
]

export const withCsvSchemaRow = (type: string, version: number, row: string[]): string[] => [
  type,
  version.toString(),
  ...row
]

const formatCsvSchemaVersion = (version: number) => `v${version}`

const getCsvSchemaValues = (
  rows: string[][],
  typeIndex: number,
  versionIndex: number
): { types: Set<string>; versions: Set<string> } => {
  const types = new Set<string>()
  const versions = new Set<string>()

  rows.forEach((row) => {
    const type = (row[typeIndex] ?? '').trim()
    const version = (row[versionIndex] ?? '').trim()
    if (type) {
      types.add(type)
    }
    if (version) {
      versions.add(version)
    }
  })

  return { types, versions }
}

export const validateCsvSchema = (
  header: string[],
  dataRows: string[][],
  options: CsvSchemaValidationOptions
): CsvSchemaValidationResult => {
  const allowLegacy = options.allowLegacy ?? true
  const normalizedHeader = header.map(normalizeText)
  const typeIndex = normalizedHeader.indexOf(normalizeText(CSV_SCHEMA_TYPE_COLUMN))
  const versionIndex = normalizedHeader.indexOf(normalizeText(CSV_SCHEMA_VERSION_COLUMN))
  const hasSchemaType = typeIndex >= 0
  const hasSchemaVersion = versionIndex >= 0

  if (!hasSchemaType && !hasSchemaVersion) {
    if (!allowLegacy) {
      throw new Error(`${options.entityName} CSV is missing ${CSV_SCHEMA_TYPE_COLUMN} and ${CSV_SCHEMA_VERSION_COLUMN}. Export a fresh CSV from this page and import that version.`)
    }
    return {
      warning: `${options.entityName} CSV has no schema version. It will import with legacy header rules; export a fresh template before editing future files.`
    }
  }

  if (!hasSchemaType || !hasSchemaVersion) {
    throw new Error(`${options.entityName} CSV must include both ${CSV_SCHEMA_TYPE_COLUMN} and ${CSV_SCHEMA_VERSION_COLUMN}.`)
  }

  if (dataRows.some((row) => !(row[typeIndex] ?? '').trim() || !(row[versionIndex] ?? '').trim())) {
    throw new Error(`${options.entityName} CSV has rows missing schema values. Export a fresh CSV from this page and import that version.`)
  }

  const { types, versions } = getCsvSchemaValues(dataRows, typeIndex, versionIndex)
  if (types.size === 0 || versions.size === 0) {
    throw new Error(`${options.entityName} CSV is missing schema values. Export a fresh CSV from this page and import that version.`)
  }
  if (types.size > 1) {
    throw new Error(`${options.entityName} CSV contains mixed ${CSV_SCHEMA_TYPE_COLUMN} values.`)
  }
  if (versions.size > 1) {
    throw new Error(`${options.entityName} CSV contains mixed ${CSV_SCHEMA_VERSION_COLUMN} values.`)
  }

  const type = [...types][0]
  const versionText = [...versions][0]
  if (type !== options.expectedType) {
    throw new Error(`This CSV is for ${type}, but this page imports ${options.expectedType}.`)
  }

  const version = Number.parseInt(versionText, 10)
  if (!Number.isFinite(version) || version.toString() !== versionText) {
    throw new Error(`${options.entityName} CSV has an invalid schema version.`)
  }
  if (version !== options.currentVersion) {
    throw new Error(`${options.entityName} CSV is ${formatCsvSchemaVersion(version)}, but this page imports ${formatCsvSchemaVersion(options.currentVersion)}. Export a fresh CSV from this page and copy your data into the current template.`)
  }

  return {}
}

export const validateCsvSchemaFromMaps = (
  rows: Array<Record<string, string>>,
  options: CsvSchemaValidationOptions
): CsvSchemaValidationResult => {
  const allowLegacy = options.allowLegacy ?? true
  const typeKey = normalizeText(CSV_SCHEMA_TYPE_COLUMN)
  const versionKey = normalizeText(CSV_SCHEMA_VERSION_COLUMN)
  const hasSchemaType = rows.some((row) => Object.prototype.hasOwnProperty.call(row, typeKey))
  const hasSchemaVersion = rows.some((row) => Object.prototype.hasOwnProperty.call(row, versionKey))

  if (!hasSchemaType && !hasSchemaVersion) {
    if (!allowLegacy) {
      throw new Error(`${options.entityName} CSV is missing ${CSV_SCHEMA_TYPE_COLUMN} and ${CSV_SCHEMA_VERSION_COLUMN}. Export a fresh CSV from this page and import that version.`)
    }
    return {
      warning: `${options.entityName} CSV has no schema version. It will import with legacy header rules; export a fresh template before editing future files.`
    }
  }

  if (!hasSchemaType || !hasSchemaVersion) {
    throw new Error(`${options.entityName} CSV must include both ${CSV_SCHEMA_TYPE_COLUMN} and ${CSV_SCHEMA_VERSION_COLUMN}.`)
  }

  if (rows.some((row) => !row[typeKey]?.trim() || !row[versionKey]?.trim())) {
    throw new Error(`${options.entityName} CSV has rows missing schema values. Export a fresh CSV from this page and import that version.`)
  }

  const types = new Set<string>()
  const versions = new Set<string>()
  rows.forEach((row) => {
    const type = row[typeKey]?.trim()
    const version = row[versionKey]?.trim()
    if (type) {
      types.add(type)
    }
    if (version) {
      versions.add(version)
    }
  })

  if (types.size === 0 || versions.size === 0) {
    throw new Error(`${options.entityName} CSV is missing schema values. Export a fresh CSV from this page and import that version.`)
  }
  if (types.size > 1) {
    throw new Error(`${options.entityName} CSV contains mixed ${CSV_SCHEMA_TYPE_COLUMN} values.`)
  }
  if (versions.size > 1) {
    throw new Error(`${options.entityName} CSV contains mixed ${CSV_SCHEMA_VERSION_COLUMN} values.`)
  }

  const type = [...types][0]
  const versionText = [...versions][0]
  if (type !== options.expectedType) {
    throw new Error(`This CSV is for ${type}, but this page imports ${options.expectedType}.`)
  }

  const version = Number.parseInt(versionText, 10)
  if (!Number.isFinite(version) || version.toString() !== versionText) {
    throw new Error(`${options.entityName} CSV has an invalid schema version.`)
  }
  if (version !== options.currentVersion) {
    throw new Error(`${options.entityName} CSV is ${formatCsvSchemaVersion(version)}, but this page imports ${formatCsvSchemaVersion(options.currentVersion)}. Export a fresh CSV from this page and copy your data into the current template.`)
  }

  return {}
}

/** Parse a string as a boolean. */
export const parseBooleanValue = (value: string): boolean => {
  const normalized = value.trim().toLowerCase()
  return ['1', 'true', 'yes', 'y', 'on'].includes(normalized)
}

/** Parse a string as a number or return null if empty/invalid. */
export const parseNumberOrNull = (value: string): number | null => {
  const normalized = value.trim().replace(/,/g, '')
  if (!normalized) {
    return null
  }
  const parsed = Number(normalized)
  return Number.isFinite(parsed) ? parsed : null
}

/** Build a CSV string from header + rows and trigger a browser download. */
export const downloadCsv = (header: string[], rows: string[][], filename: string) => {
  const csv = [header, ...rows]
    .map((columns) => columns.map(escapeCsvValue).join(','))
    .join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}

/** Read a CSV file and return { header, dataRows, hasHeader }. */
export const readCsvFile = async (
  file: File,
  requiredHeaders: string[]
): Promise<{ header: string[]; dataRows: string[][]; hasHeader: boolean }> => {
  const text = await file.text()
  const rows = parseCsvText(text)
  if (rows.length === 0) {
    throw new Error('CSV file is empty.')
  }

  const header = rows[0].map((value) => value.trim())
  const hasHeader = requiredHeaders.every((req) =>
    header.some((col) => normalizeText(col) === normalizeText(req))
  )

  const dataRows = hasHeader ? rows.slice(1) : rows
  if (dataRows.length === 0) {
    throw new Error('CSV appears to contain only headers.')
  }

  return { header, dataRows, hasHeader }
}

/**
 * Build a column-index lookup from a header row using alias mappings.
 * Returns a partial record mapping column keys to their 0-based index in the header.
 */
export const buildHeaderIndex = <K extends string>(
  header: string[],
  aliases: Record<K, string[]>
): Partial<Record<K, number>> => {
  const normalizedHeader = header.reduce<Record<string, number>>((acc, column, index) => {
    acc[normalizeText(column)] = index
    return acc
  }, {})

  return (Object.keys(aliases) as K[]).reduce<Partial<Record<K, number>>>((acc, key) => {
    const keyAliases = aliases[key]
    const mappedIndex = keyAliases
      .map((alias) => normalizedHeader[alias])
      .find((candidate) => candidate !== undefined)

    if (typeof mappedIndex === 'number') {
      acc[key] = mappedIndex
    }

    return acc
  }, {})
}

/**
 * Create a getValue helper that resolves column values from a row
 * using header index with fallback to positional index.
 */
export const makeGetValue = <K extends string>(
  headerIndex: Partial<Record<K, number>> | null,
  fallbackIndex: Record<K, number>
) => {
  return (row: string[], key: K): string => {
    const headerIndexValue = headerIndex?.[key]
    const index = typeof headerIndexValue === 'number' ? headerIndexValue : fallbackIndex[key]
    return (row[index] ?? '').trim()
  }
}
